import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GoogleMap, LoadScript, Circle, Marker, InfoWindow } from '@react-google-maps/api';
import { MapPin, TrendingUp, Package, DollarSign, Calendar, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBslmdWaiWkD85mWtZY8loRATqVgKM_K6g';

const mapContainerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: -23.5505,
  lng: -46.6333
};

// Sem a lib "visualization" — Google removeu HeatmapLayer da Maps JS API v3.65 (mai/2026).
// Usamos Circle overlays agrupados por proximidade para simular o efeito de calor.
const libraries = [];

// Gradiente de calor (frio -> quente)
const HEAT_PALETTE = [
  { stop: 0.0, color: '#3b82f6' },   // azul
  { stop: 0.25, color: '#10b981' },  // verde
  { stop: 0.5, color: '#f59e0b' },   // amarelo
  { stop: 0.75, color: '#f97316' },  // laranja
  { stop: 1.0, color: '#ef4444' },   // vermelho
];

function pickColor(intensity) {
  // intensity ∈ [0,1]
  for (let i = 1; i < HEAT_PALETTE.length; i++) {
    if (intensity <= HEAT_PALETTE[i].stop) {
      return HEAT_PALETTE[i].color;
    }
  }
  return HEAT_PALETTE[HEAT_PALETTE.length - 1].color;
}

export function HeatmapPage() {
  const [loading, setLoading] = useState(true);
  const [deliveryData, setDeliveryData] = useState([]);
  const [period, setPeriod] = useState('30');
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    totalRevenue: 0,
    avgTicket: 0,
    topNeighborhoods: []
  });
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapLoaded, setMapLoaded] = useState(false);

  const fetchDeliveryData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/analytics/heatmap', {
        params: { days: parseInt(period) }
      });
      
      const data = response.data;
      setDeliveryData(data.deliveries || []);
      setStats({
        totalDeliveries: data.total_deliveries || 0,
        totalRevenue: data.total_revenue || 0,
        avgTicket: data.avg_ticket || 0,
        topNeighborhoods: data.top_neighborhoods || []
      });
      
      // Ajustar centro do mapa baseado nos dados
      if (data.deliveries && data.deliveries.length > 0) {
        const validPoints = data.deliveries.filter(d => d.lat && d.lng);
        if (validPoints.length > 0) {
          const avgLat = validPoints.reduce((sum, d) => sum + d.lat, 0) / validPoints.length;
          const avgLng = validPoints.reduce((sum, d) => sum + d.lng, 0) / validPoints.length;
          setMapCenter({ lat: avgLat, lng: avgLng });
        }
      }
      
      toast.success(`${data.total_deliveries || 0} entregas carregadas`);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do heatmap');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDeliveryData();
  }, [fetchDeliveryData]);

  // Agrupa pontos próximos por arredondamento (~110m no equador) para evitar
  // milhares de overlays. Cada cluster vira um Circle com intensidade = soma do total.
  const heatClusters = useMemo(() => {
    const buckets = new Map();
    for (const d of deliveryData) {
      if (!d.lat || !d.lng) continue;
      // 3 casas decimais ≈ 100m de precisão
      const key = `${d.lat.toFixed(3)},${d.lng.toFixed(3)}`;
      const prev = buckets.get(key);
      if (prev) {
        prev.weight += Math.max(d.total || 1, 1);
        prev.count += 1;
      } else {
        buckets.set(key, {
          lat: d.lat,
          lng: d.lng,
          weight: Math.max(d.total || 1, 1),
          count: 1,
        });
      }
    }
    const list = Array.from(buckets.values());
    if (list.length === 0) return [];
    const maxW = Math.max(...list.map(c => c.weight));
    return list.map(c => ({
      ...c,
      intensity: maxW > 0 ? c.weight / maxW : 0,
    }));
  }, [deliveryData]);

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <MainLayout>
      <div className="space-y-6 page-enter" data-testid="heatmap-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              Mapa de Calor
            </h1>
            <p className="text-slate-600">Visualize a concentração de entregas por região</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="15">Últimos 15 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="60">Últimos 60 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={fetchDeliveryData} 
              disabled={loading}
              variant="outline"
              data-testid="refresh-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-700">Total Entregas</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.totalDeliveries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Faturamento</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(stats.totalRevenue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-purple-700">Ticket Médio</p>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(stats.avgTicket)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-orange-700">Bairros Atendidos</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.topNeighborhoods.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map and Rankings */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Heatmap */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  Mapa de Calor de Entregas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading && !mapLoaded ? (
                  <div className="flex items-center justify-center h-[600px] bg-slate-100 rounded-xl">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-red-500 mx-auto mb-2" />
                      <p className="text-slate-600">Carregando mapa...</p>
                    </div>
                  </div>
                ) : (
                  <LoadScript 
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY} 
                    libraries={libraries}
                  >
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={13}
                      onLoad={handleMapLoad}
                      options={{
                        styles: [
                          {
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }]
                          }
                        ],
                        mapTypeControl: true,
                        streetViewControl: false,
                        fullscreenControl: true
                      }}
                    >
                      {mapLoaded && heatClusters.map((c, i) => (
                        <Circle
                          key={`heat-${i}`}
                          center={{ lat: c.lat, lng: c.lng }}
                          radius={150 + c.intensity * 220}
                          options={{
                            strokeColor: pickColor(c.intensity),
                            strokeOpacity: 0.45,
                            strokeWeight: 1,
                            fillColor: pickColor(c.intensity),
                            fillOpacity: 0.25 + c.intensity * 0.45,
                            clickable: false,
                            zIndex: Math.round(c.intensity * 100),
                          }}
                        />
                      ))}
                      
                      {/* Marcadores para os top bairros */}
                      {stats.topNeighborhoods.slice(0, 5).map((neighborhood, index) => (
                        neighborhood.lat && neighborhood.lng && (
                          <Marker
                            key={index}
                            position={{ lat: neighborhood.lat, lng: neighborhood.lng }}
                            label={{
                              text: String(index + 1),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                            onClick={() => setSelectedPoint(neighborhood)}
                          />
                        )
                      ))}
                      
                      {selectedPoint && (
                        <InfoWindow
                          position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                          onCloseClick={() => setSelectedPoint(null)}
                        >
                          <div className="p-2">
                            <h3 className="font-bold text-slate-900">{selectedPoint.neighborhood}</h3>
                            <p className="text-sm text-slate-600">{selectedPoint.count} entregas</p>
                            <p className="text-sm text-green-600 font-medium">
                              {formatCurrency(selectedPoint.revenue)}
                            </p>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </LoadScript>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Neighborhoods */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Top Bairros
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="animate-pulse">
                        <div className="h-16 bg-slate-100 rounded-lg"></div>
                      </div>
                    ))}
                  </div>
                ) : stats.topNeighborhoods.length > 0 ? (
                  <div className="space-y-3 max-h-[550px] overflow-y-auto">
                    {stats.topNeighborhoods.map((item, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                          index === 0 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200' :
                          index === 1 ? 'bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200' :
                          index === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200' :
                          'bg-white border-slate-100'
                        }`}
                        onClick={() => {
                          if (item.lat && item.lng) {
                            setMapCenter({ lat: item.lat, lng: item.lng });
                            setSelectedPoint(item);
                          }
                        }}
                        data-testid={`neighborhood-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            index === 0 ? 'bg-yellow-500' :
                            index === 1 ? 'bg-slate-400' :
                            index === 2 ? 'bg-orange-400' :
                            'bg-slate-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {item.neighborhood || 'Sem bairro'}
                            </p>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-500">{item.count} entregas</span>
                              <span className="text-green-600 font-medium">
                                {formatCurrency(item.revenue)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nenhuma entrega encontrada no período</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default HeatmapPage;
